// src/pages/OrderManagement/Rules.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  FaBars,
  FaHome,
  FaCalendarAlt,
  FaFileInvoiceDollar,
  FaChartBar,
  FaFileAlt,
  FaCheckCircle,
  FaExclamationTriangle,
  FaBuilding,
  FaTasks,
  FaWarehouse,
  FaUsers,
  FaSave,
  FaUndo,
  FaPrint,
  FaTrash,
  FaSyncAlt,
  FaArrowUp,
} from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API = "https://nissi-india.com/api/rules";

const toInt = (v, def = 0) => {
  const n = Number(String(v ?? "").trim());
  return Number.isFinite(n) ? n : def;
};

export default function Rules() {
  const location = useLocation();

  const [isDark, setIsDark] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // ✅ safe localStorage helper
  const getLS = (keys, fallback = "") => {
    if (typeof window === "undefined") return fallback;

    const keyList = Array.isArray(keys) ? keys : [keys];
    for (const key of keyList) {
      const val = localStorage.getItem(key);
      if (val !== null && val !== undefined && String(val).trim() !== "") {
        return String(val).trim();
      }
    }
    return fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS(["finYearId"]);
  const finYear = getLS(["finYear"]);
  const branchIdLS = getLS(["branchId", "branchid"]);
  const branchTypeLS = getLS(["branchType", "branchtype"]);
  const userIdLS = getLS(["userId", "loginuserid", "loginUserId"]);

  // ✅ parsed values
  const branchId = useMemo(() => toInt(branchIdLS, 0), [branchIdLS]);
  const branchType = useMemo(
    () => String(branchTypeLS || "").trim(),
    [branchTypeLS]
  );
  const loginUserId = useMemo(() => toInt(userIdLS, 0), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId || "",
      "x-finyear": finYear || "",
      "x-branchid": String(branchId || 0),
      "x-branchtype": branchType || "",
      "x-userid": String(loginUserId || 0),
    }),
    [finYearId, finYear, branchId, branchType, loginUserId]
  );

  const contractId = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    const fromUrl = qs.get("contractId") || qs.get("contractid") || "";
    const fromSession =
      sessionStorage.getItem("Selcontractid") ||
      sessionStorage.getItem("selcontractid") ||
      sessionStorage.getItem("contractId") ||
      "";
    const cid = toInt(fromUrl || fromSession || 0);
    return cid;
  }, [location.search]);

  useEffect(() => {
    if (contractId) sessionStorage.setItem("Selcontractid", String(contractId));
  }, [contractId]);

  const sidebarItems = [
    { name: "Date/LOA", icon: FaCalendarAlt, path: "/OrderManagement/ProjectEntry" },
    { name: "BOQ/Operative", icon: FaFileInvoiceDollar, path: "/OrderManagement/BOQ" },
    { name: "Program/DPR", icon: FaChartBar, path: "/OrderManagement/DailyProgressMonthlyWise" },
    { name: "Purchase Order", icon: FaFileAlt, path: "/OrderManagement/PurchaseOrderListing" },
    { name: "Rate Approval", icon: FaCheckCircle, path: "/OrderManagement/RateApprove" },
    { name: "Risk/Issues", icon: FaExclamationTriangle, path: "/OrderManagement/RiskAndIssue" },
    { name: "Documentation", icon: FaFileAlt, path: "/OrderManagement/ProjectEntryDocumentation" },
    { name: "Photos", icon: FaBuilding, path: "/OrderManagement/PhotoDocument" },
    { name: "Reconciliation", icon: FaTasks, path: "/OrderManagement/Reconciliation" },
    { name: "Do/Don't's", icon: FaWarehouse, path: "/OrderManagement/Rules", active: true },
    { name: "Vendor Details", icon: FaUsers, path: "/OrderManagement/ProjectVendorDetails" },
  ];

  // ===== form
  const [rulesId, setRulesId] = useState(0);
  const [title, setTitle] = useState("");
  const [doText, setDoText] = useState("");
  const [dontText, setDontText] = useState("");

  const clearForm = () => {
    setRulesId(0);
    setTitle("");
    setDoText("");
    setDontText("");
  };

  // ===== list
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const loadRules = async () => {
    if (!contractId) {
      toast.error("contractId missing");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(`${API}/view?contractId=${contractId}`, {
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load");
        return;
      }

      setRows(res.data?.rows || []);
      setSelectedIds(new Set());
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, commonHeaders]);

  const validate = () => {
    if (!title.trim()) {
      toast.error("Please enter Title..");
      return false;
    }
    return true;
  };

  const save = async () => {
    if (!contractId) {
      toast.error("contractId missing");
      return;
    }

    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        contractId,
        rulesId: rulesId || 0,
        title,
        doText,
        dontText,
      };

      const res = await axios.post(`${API}/save`, payload, {
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Save failed");
        return;
      }

      toast.success(res.data?.message || "Saved");
      await loadRules();
      clearForm();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  const openForEdit = async (id) => {
    const rid = toInt(id);
    if (!rid) return;

    setLoading(true);
    try {
      const res = await axios.get(`${API}/get?rulesId=${rid}`, {
        headers: commonHeaders,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Load failed");
        return;
      }

      const r = res.data?.row || {};
      setRulesId(toInt(r.fld_rulesid));
      setTitle(r.fld_Title || "");
      setDoText(r.fld_Do || "");
      setDontText(r.fld_Dont || "");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  const toggleAll = (checked) => {
    if (checked) {
      setSelectedIds(new Set(rows.map((r) => toInt(r.fld_rulesid))));
    } else {
      setSelectedIds(new Set());
    }
  };

  const toggleOne = (id, checked) => {
    const rid = toInt(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(rid);
      else next.delete(rid);
      return next;
    });
  };

  const removeSelected = async () => {
    if (!selectedIds.size) {
      toast.error("Please select any one...");
      return;
    }

    const ok = window.confirm("Are you sure to delete?");
    if (!ok) return;

    setLoading(true);
    try {
      const ids = Array.from(selectedIds);

      const res = await axios.delete(`${API}/delete`, {
        headers: commonHeaders,
        data: { ids },
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Delete failed");
        return;
      }

      toast.success(res.data?.message || "Removed");
      clearForm();
      await loadRules();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  const print = () => {
    if (!contractId) {
      toast.error("contractId missing");
      return;
    }

    window.open(
      `/OrderManagement/RulesPrint?contractId=${contractId}`,
      "_blank",
      "width=1200,height=900,scrollbars=yes"
    );
  };

  // ===== green up arrow
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 250);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className={`min-h-screen transition-all duration-300 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
      <ToastContainer position="top-right" autoClose={2500} />

      {/* Top Bar */}
      <div className={`sticky top-0 z-50 backdrop-blur-md border-b ${isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"} shadow-lg`}>
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className={`p-2 rounded-lg transition-colors ${isDark ? "hover:bg-gray-700 text-gray-300" : "hover:bg-gray-100 text-gray-600"}`}
              >
                <FaBars size={20} />
              </button>

              <div className="flex items-center gap-3">
                <FaHome className="text-blue-600 text-xl" />
                <h1 className={`text-xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Project Management System
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                disabled={loading}
                onClick={loadRules}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 ${
                  loading ? "bg-blue-400 text-white cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
              >
                <FaSyncAlt />
                Refresh
              </button>

              <button
                onClick={() => setIsDark(!isDark)}
                className={`p-2 rounded-lg transition-colors ${isDark ? "bg-yellow-600 hover:bg-yellow-700 text-white" : "bg-gray-800 hover:bg-gray-900 text-white"}`}
              >
                {isDark ? "☀️" : "🌙"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className={`${sidebarOpen ? "w-64" : "w-16"} transition-all duration-300 ${isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"} border-r min-h-screen`}>
          <div className="p-4">
            {sidebarItems.map((item, index) => (
              <Link
                to={`${item.path}?contractId=${contractId || ""}`}
                key={index}
                className={`flex items-center gap-3 p-3 rounded-lg mb-2 cursor-pointer transition-all duration-200 ${
                  window.location.pathname === item.path
                    ? isDark
                      ? "bg-blue-600 text-white"
                      : "bg-blue-500 text-white"
                    : isDark
                    ? "hover:bg-gray-700 text-gray-300"
                    : "hover:bg-gray-100 text-gray-700"
                }`}
              >
                <item.icon size={18} />
                {sidebarOpen && <span className="text-sm font-medium">{item.name}</span>}
              </Link>
            ))}
          </div>
        </div>

        {/* Main */}
        <div className="flex-1">
          <div className={`transition-all duration-300 ${isDark ? "bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900" : "bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"}`}>
            {/* Header */}
            <div className={`backdrop-blur-md border-b ${isDark ? "bg-gray-900/80 border-gray-700" : "bg-white/80 border-gray-200"}`}>
              <div className="px-6 py-4">
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
                  <div>
                    <h1 className={`text-2xl lg:text-3xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                      DO&apos;S / DON&apos;TS
                    </h1>
                    <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      ContractId: <b>{contractId || "-"}</b>
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={print}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105"
                    >
                      <FaPrint />
                      Print
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Form */}
              <div className={`rounded-2xl shadow-xl border ${isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"}`}>
                <div className="bg-blue-600 text-white px-6 py-3 rounded-t-2xl">
                  <h2 className="text-lg font-bold">{rulesId ? "EDIT RULE" : "ADD RULE"}</h2>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={`text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                        Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className={`mt-1 w-full px-3 py-2 rounded-lg border outline-none ${
                          isDark ? "bg-gray-900/30 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"
                        }`}
                        placeholder="Enter title"
                      />
                    </div>

                    <div>
                      <label className={`text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                        Do&apos;s
                      </label>
                      <input
                        value={doText}
                        onChange={(e) => setDoText(e.target.value)}
                        className={`mt-1 w-full px-3 py-2 rounded-lg border outline-none ${
                          isDark ? "bg-gray-900/30 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"
                        }`}
                        placeholder="Enter do's"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className={`text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                        Don&apos;t Do&apos;s
                      </label>
                      <input
                        value={dontText}
                        onChange={(e) => setDontText(e.target.value)}
                        className={`mt-1 w-full px-3 py-2 rounded-lg border outline-none ${
                          isDark ? "bg-gray-900/30 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"
                        }`}
                        placeholder="Enter don't do's"
                      />
                    </div>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-3 justify-center">
                    <button
                      disabled={loading}
                      onClick={save}
                      className={`px-5 py-2 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 ${
                        loading ? "bg-green-400 text-white cursor-not-allowed" : "bg-green-600 hover:bg-green-700 text-white"
                      }`}
                    >
                      <FaSave />
                      {rulesId ? "Update" : "Submit"}
                    </button>

                    <button
                      disabled={loading}
                      onClick={clearForm}
                      className="px-5 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white flex items-center gap-2 transition-all duration-200 hover:scale-105"
                    >
                      <FaUndo />
                      Add New
                    </button>
                  </div>
                </div>
              </div>

              {/* List */}
              <div className={`rounded-2xl shadow-xl border ${isDark ? "bg-gray-800/50 backdrop-blur-md border-gray-700" : "bg-white border-gray-200"}`}>
                <div className="bg-blue-600 text-white px-6 py-3 rounded-t-2xl flex items-center justify-between">
                  <h2 className="text-lg font-bold">RULES LIST</h2>

                  <button
                    disabled={loading}
                    onClick={removeSelected}
                    className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 ${
                      loading ? "bg-rose-400 text-white cursor-not-allowed" : "bg-rose-600 hover:bg-rose-700 text-white"
                    }`}
                    title="Remove Selected"
                  >
                    <FaTrash />
                    Delete
                  </button>
                </div>

                <div className="p-4">
                  <div
                    className={`rounded-xl border ${isDark ? "border-gray-700" : "border-gray-200"}`}
                    style={{ maxHeight: "65vh", overflow: "auto" }}
                  >
                    <table className="w-full text-sm min-w-[900px]">
                      <thead className={`${isDark ? "bg-gray-900/40" : "bg-gray-50"} sticky top-0 z-10`}>
                        <tr className={`${isDark ? "text-gray-200" : "text-gray-800"}`}>
                          <th className="p-3 text-center w-[70px]">S.No</th>
                          <th className="p-3 text-center w-[90px]">Select</th>
                          <th className="p-3 text-left w-[240px]">Title</th>
                          <th className="p-3 text-left">Do&apos;s</th>
                          <th className="p-3 text-left text-red-600">Don&apos;t Do&apos;s</th>
                        </tr>
                      </thead>

                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan={5} className="p-6 text-center">
                              Loading...
                            </td>
                          </tr>
                        ) : rows.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="p-6 text-center">
                              No Records Found
                            </td>
                          </tr>
                        ) : (
                          rows.map((r, idx) => {
                            const id = toInt(r.fld_rulesid);
                            const checked = selectedIds.has(id);

                            return (
                              <tr key={id} className={`${isDark ? "border-gray-700 text-gray-200" : "border-gray-200 text-gray-800"} border-t`}>
                                <td className="p-3 text-center">{idx + 1}</td>

                                <td className="p-3 text-center">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => toggleOne(id, e.target.checked)}
                                  />
                                </td>

                                <td className="p-3">
                                  <button
                                    onClick={() => openForEdit(id)}
                                    className={`font-semibold underline-offset-2 hover:underline ${
                                      isDark ? "text-blue-300" : "text-blue-700"
                                    }`}
                                    title="Click to edit"
                                  >
                                    {r.fld_Title}
                                  </button>
                                </td>

                                <td className="p-3">{r.fld_Do}</td>
                                <td className="p-3 text-red-600">{r.fld_Dont}</td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {rows.length > 0 && (
                    <div className="mt-3 flex items-center justify-between">
                      <label className={`flex items-center gap-2 ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                        <input
                          type="checkbox"
                          checked={rows.length > 0 && selectedIds.size === rows.length}
                          onChange={(e) => toggleAll(e.target.checked)}
                        />
                        Select All
                      </label>

                      <div className={`${isDark ? "text-gray-300" : "text-gray-600"} text-xs`}>
                        Selected: <b>{selectedIds.size}</b>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Green Up Arrow */}
      {showTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-6 right-6 w-12 h-12 rounded-full bg-green-600 hover:bg-green-700 text-white shadow-xl flex items-center justify-center"
          title="Back to top"
          type="button"
        >
          <FaArrowUp />
        </button>
      )}
    </div>
  );
} 